<template>
  <div>
    <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
    <div class="d-md-none repositories-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />

      <div
        v-for="(item, index) in paginatedRepositories"
        :key="item._id"
        class="repositories-mobile-card"
        :class="{
          'repositories-mobile-card--selected': index === activeRow,
        }"
        @click="activeRow = activeRow === index ? null : index"
      >
        <div class="repositories-mobile-card__main">
          <div class="repositories-mobile-card__info">
            <div class="repositories-mobile-card__info-top">
              <div class="font-weight-bold text-primary text-body-2">
                {{ item.name }}
              </div>
              <div class="text-caption mt-1">
                <v-chip size="small">
                  {{ item.institution_id?.title }}
                </v-chip>
              </div>
            </div>
          </div>
        </div>

        <div class="repositories-mobile-card__actions">
          <v-spacer />
          <v-btn icon variant="text" size="small" @click.stop="viewDetails(item)">
            <v-icon size="20" color="grey-darken-2">
              mdi-format-list-bulleted
            </v-icon>
          </v-btn>
          <v-btn
            v-if="canEditDelete"
            icon
            variant="text"
            size="small"
            @click.stop="editRepository(item)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="canEditDelete"
            icon
            variant="text"
            size="small"
            @click.stop="deleteRepository(item._id)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>

      <v-alert
        v-if="!loading && paginatedRepositories.length < 1"
        type="info"
        variant="tonal"
        class="mt-2"
      >
        No data available.
      </v-alert>
    </div>
    <!-- ===================================================================== -->

    <div class="repositories-table-host mb-5">
      <v-data-table
        v-model:options="pagination"
        :headers="headers"
        :items="filteredRepositories"
        :search="searchField"
        :loading="loading"
        item-value="name"
        :items-per-page="pagination.itemsPerPage"
        :items-per-page-options="[
          { value: 5, title: '5' },
          { value: 10, title: '10' },
          { value: 25, title: '25' },
          { value: 50, title: '50' },
        ]"
        @update:options="updatePagination"
      >
        <template #progress>
          <v-progress-linear color="red" indeterminate />
        </template>
        <template #item="{ item, index }">
          <tr
            :class="{ tableRowColorSelected: index == activeRow }"
            class="tableRowColor"
            @click="activeRow = activeRow === index ? null : index"
          >
            <td class="text-xs">
              {{ item.name }}
            </td>
            <td class="text-xs">
              <v-chip>
                {{ item.institution_id.title }}
              </v-chip>
            </td>
            <td class="repository-actions-cell">
              <div class="repository-actions d-flex flex-nowrap align-center">
                <v-icon
                  class="mr-2 pointer flex-shrink-0"
                  color="grey-darken-2"
                  @click.stop="viewDetails(item)"
                >
                  mdi-format-list-bulleted
                </v-icon>
                <v-icon
                  v-if="canEditDelete"
                  class="mr-2 pointer flex-shrink-0"
                  color="grey-darken-2"
                  @click.stop="editRepository(item)"
                >
                  mdi-pencil
                </v-icon>
                <v-icon
                  v-if="canEditDelete"
                  class="pointer flex-shrink-0"
                  color="grey-darken-2"
                  @click.stop="deleteRepository(item._id)"
                >
                  mdi-delete
                </v-icon>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import getRequest from "../../api/getRequest";

export default {
  data: () => ({
    loading: false,
    searchField: "",
    activeRow: null,

    pagination: {
      itemsPerPage: 10,
      page: 1,
      groupBy: ["name"],
      sortBy: { key: "name", order: "desc" },
      sortDesc: [true],
    },
    headers: [
      {
        title: "Name",
        sortable: false,
        key: "name",
      },
      {
        title: "Institution ID",
        sortable: false,
        key: "institution_id",
      },
      {
        title: "Actions",
        sortable: false,
        key: "",
      },
    ],
    repositories: [],
    filteredRepositories: [],
  }),
  computed: {
    canEditDelete() {
      return (
        this.$store.getters.role == "LekoAdmin" ||
        this.$store.getters.role == "InstitutionAdmin" ||
        this.$store.getters.role == "InstitutionManager" ||
        this.$store.getters.role == "SuperAdmin"
      );
    },
    paginatedRepositories() {
      const page = this.pagination.page || 1;
      const perPage = this.pagination.itemsPerPage || 10;
      const start = (page - 1) * perPage;
      return this.filteredRepositories.slice(start, start + perPage);
    },
  },
  created() {
    this.getLocale();
  },
  beforeMount() {
    this.getRepositories();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("repositories.table.name");
      this.headers[1].title = this.$t("repositories.table.institution_id");
      this.headers[2].title = this.$t("repositories.table.actions");
    },
    search(value) {
      this.searchField = value;
      if (!value) {
        this.filteredRepositories = this.repositories;
        return;
      }

      const term = value.toLowerCase();
      this.filteredRepositories = this.repositories.filter(
        (repo) =>
          repo.name?.toLowerCase().includes(term) ||
          repo.institution_id?.title?.toLowerCase().includes(term),
      );
    },
    getRepositories() {
      this.loading = true;
      getRequest(
        "/repository/new",
        `role=${this.$store.getters.role}&institution_id=${this.$store.getters.institutionId}`,
      )
        .then((response) => {
          this.repositories = response.repositories;
          this.filteredRepositories = response.repositories;
          this.pagination.page = 1;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    updatePagination(options) {
      this.pagination = {
        ...this.pagination,
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 10,
        page: options.page ? options.page : 1,
      };
      this.activeRow = null;
    },
    viewDetails(data) {
      this.$emit("details", data);
    },
    editRepository(data) {
      this.$emit("edit", data);
    },
    deleteRepository(id) {
      this.$emit("delete", id);
    },
  },
};
</script>

<style scoped>
@media (max-width: 959.98px) {
  .repositories-table-host :deep(.v-table__wrapper),
  .repositories-table-host :deep(.v-data-table__wrapper),
  .repositories-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .repositories-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .repositories-mobile-list {
    display: none !important;
  }
}

.repositories-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.repositories-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.repositories-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 10px 10px 6px;
  gap: 8px;
}
.repositories-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.repositories-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 4px 6px;
}

.repository-actions-cell {
  white-space: nowrap;
}

.repository-actions {
  flex-wrap: nowrap;
}
</style>
